/**
 * The small shapes the theory layer repeats everywhere.
 *
 * Every one of these mirrors a serializer in `arranger_ai_pipeline.api.serial` exactly. The
 * Python side owns the numbers; nothing here is allowed to recompute or re-round one.
 */

/** The eight predicates, in the order `rk_check.PREDICATES` declares them. */
export const PREDICATES = [
  "compass",
  "spacing",
  "doubling",
  "balance",
  "progression",
  "continuity",
  "voices",
  "melody",
] as const;

export type Predicate = (typeof PREDICATES)[number];

/** An inclusive pitch span, as MIDI numbers and as the names an engraver would print. */
export interface Compass {
  low: number;
  high: number;
  lowName: string;
  highName: string;
}

/**
 * One predicate's opinion of one score.
 *
 * `score` is `null` when the predicate judged nothing — silence is not approval, so it must
 * render as an em dash, never as a pass.
 */
export interface Judgement {
  predicate: Predicate;
  judged: number;
  clean: number;
  score: number | null;
  faults: string[];
}

/** All eight predicates over one score, with the averaged rate the book is scored by. */
export interface Verdict {
  example: string;
  score: number | null;
  coverage: number | null;
  quality: number | null;
  notes: number;
  checked: number;
  graded: number;
  faultCount: number;
  silent: Predicate[];
  judgements: Judgement[];
}

/** One predicate's share of the whole corpus, from the snapshot. */
export interface PredicateTotal {
  predicate: Predicate;
  judged: number;
  clean: number;
  spokeIn: number;
  faults: number;
  score: number | null;
}

/** A part as the MusicXML declares it. */
export interface ScorePart {
  partId: string;
  name: string | null;
  instrument: string | null;
  family: string | null;
  transpose: number;
  pizzicato: boolean;
  solo: boolean;
}

/** The heading a reader needs above a verdict: metre, key, length, who plays. */
export interface ScoreMeta {
  title: string | null;
  measures: number;
  keyFifths: number | null;
  timeSignature: string | null;
  families: string[];
  parts: ScorePart[];
}
